import {
  useState,
  useEffect,
  useMemo,
  useRef,
  type Dispatch,
  type SetStateAction,
} from "react";
import {
  Check,
  AlertTriangle,
  Plus,
  CheckCircle,
  RotateCcw,
  History,
  ArrowLeft,
} from "lucide-react";
import { useNavigate, useParams } from "@tanstack/react-router";
import { useCurrentUser } from "@/features/auth/useCurrentUser";
import { getOperationalCapabilities } from "@/features/auth/operationalCapabilities";
import {
  useCheckin,
  useAddReceipt,
  useCloseShortfall,
  useUndoReceivingAction,
  useUndoReceivingHistory,
  useUndoReturnHistory,
  useUndoCheckHistory,
  useUndoLifecycleHistory,
  useReopenShortfallItem,
  useUndoReturnAction,
  useRequestReturn,
  useDecideReturn,
  useReopenReturnDecision,
  useSupplierBoxSummaries,
  useToggleCheckinStatus,
  useCheckinAudit,
} from "@/features/checkin/hooks";
import {
  buildCheckinItemDisplay,
  selectLatestActiveReturnLot,
} from "@/features/checkin/itemDisplay";
import { getCheckinActionError } from "@/features/checkin/itemUi";
import {
  buildCheckinPageDisplay,
  filterCheckinItems,
  filterSupplierBoxSummaries,
  getCheckinAuditOperation,
  canUndoReturnHistoryEvent,
  receivingHistorySource,
  receivingHistoryUndoReason,
  checkHistoryUndoReason,
  lifecycleHistoryUndoReason,
} from "@/features/checkin/pageDisplay";
import { useCreateAlert } from "@/features/alert/hooks";
import { getUserFacingErrorMessage } from "@/api/userFacingError";
import {
  FloatingToast,
  type FloatingToastState,
  type FloatingToastTone,
} from "@/components/feedback/FloatingToast";
import { timeAgo } from "@/lib/timeAgo";
import {
  formatQuantityDisplay,
  getQuantityUnitLabel,
  isLegacyDirectKgItem,
} from "@/lib/quantity";
import type {
  EnrichedCheckinItem,
  DecideReturnPayload,
  CheckinAuditEntry,
} from "@/features/checkin/types";
import ReturnNoteModal from "@/components/checkin/ReturnNoteModal";
import { ReturnQuantityModal } from "@/components/checkin/ReturnQuantityModal";
import { ReturnHistoryBlock } from "@/components/checkin/ReturnHistoryBlock";
import { ReceivingQuantityPanel } from "@/components/checkin/ReceivingQuantityPanel";
import { ReturnResolutionModal } from "@/components/checkin/ReturnResolutionModal";
import { CheckinPageControls } from "@/components/checkin/CheckinPageControls";
import CheckinActionConfirmDialog from "@/components/checkin/CheckinActionConfirmDialog";
import { CheckinHistoryDrawer } from "@/components/checkin/CheckinHistoryDrawer";
import { CheckinLifecycleHistoryDrawer } from "@/components/checkin/CheckinLifecycleHistoryDrawer";
import { CheckinAlertDialog } from "@/components/checkin/CheckinAlertDialog";
import { SupplierBoxCard } from "@/components/checkin/SupplierBoxCard";
import {
  OperationalPageFrame,
  OperationalPageHeader,
} from "@/components/operational/OperationalPageLayout";
import "./CheckinDetailPage.css";

type LastActionKind = "receiving" | "return";

const FEEDBACK_TOAST_VISIBLE_MS = 4200;
const FEEDBACK_TOAST_EXIT_MS = 180;
const WHOLE_DIRECT_KG_QUANTITY_TOAST = {
  title: "Quantidade inválida",
  message:
    "Para itens vendidos por kg, informe um número inteiro maior que zero.",
  tone: "error" as const,
};

function compareMovementTime(left?: string | null, right?: string | null) {
  const leftMs = Date.parse(left ?? "");
  const rightMs = Date.parse(right ?? "");
  if (!Number.isFinite(leftMs) || !Number.isFinite(rightMs)) return null;
  const seconds = Math.floor(leftMs / 1000) - Math.floor(rightMs / 1000);
  if (seconds) return Math.sign(seconds);
  const nanos = (value: string) =>
    Number(
      (value.match(/\.(\d+)(?:Z|[+-]\d{2}:\d{2})$/)?.[1] ?? "0")
        .padEnd(9, "0")
        .slice(0, 9),
    );
  return Math.sign(nanos(left!) - nanos(right!));
}

type CheckinFeedbackToast = {
  title: string;
  message?: string;
  tone?: FloatingToastTone;
  state: FloatingToastState;
};

type CheckinFeedback = Omit<CheckinFeedbackToast, "state">;

function CheckinItemRow({
  item,
  checkinId,
  canManageCheckins,
  canAudit,
  currentUserRole,
  isClientView = false,
  readOnly = false,
  closed = false,
  lastUndoableItemId,
  setLastUndoableItemId,
  onFeedback,
}: {
  item: EnrichedCheckinItem;
  checkinId: string;
  canManageCheckins: boolean;
  canAudit: boolean;
  currentUserRole: string;
  isClientView?: boolean;
  readOnly?: boolean;
  closed?: boolean;
  lastUndoableItemId: string | null;
  setLastUndoableItemId: Dispatch<SetStateAction<string | null>>;
  onFeedback: (feedback: CheckinFeedback) => void;
}) {
  const [receivePanelOpen, setReceivePanelOpen] = useState(false);
  const [returnModalOpen, setReturnModalOpen] = useState(false);
  const [shortfallModalOpen, setShortfallModalOpen] = useState(false);
  const [returnResolutionOpen, setReturnResolutionOpen] = useState(false);
  const [returnDecisionCorrectionOpen, setReturnDecisionCorrectionOpen] =
    useState(false);
  const [lastActionLabel, setLastActionLabel] = useState<string | null>(null);
  const [localReturnLotId, setLocalReturnLotId] = useState<string | null>(null);
  const [lastActionKind, setLastActionKind] =
    useState<LastActionKind>("receiving");
  const [localActionPending, setLocalActionPending] = useState(false);
  const actionLock = useRef(false);
  const lastAuditRefreshMovementKey = useRef<string | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [selectedHistoryReceipt, setSelectedHistoryReceipt] =
    useState<CheckinAuditEntry | null>(null);
  const [selectedHistoryReturn, setSelectedHistoryReturn] =
    useState<CheckinAuditEntry | null>(null);
  const [selectedHistoryCheck, setSelectedHistoryCheck] =
    useState<CheckinAuditEntry | null>(null);
  const historyQuery = useCheckinAudit(
    checkinId,
    ((canAudit || currentUserRole === "checker") && historyOpen) ||
      (canAudit &&
        canManageCheckins &&
        !readOnly &&
        Boolean(item.return_lots?.length)),
  );

  const addReceiptMutation = useAddReceipt(checkinId);
  const closeShortfallMutation = useCloseShortfall(checkinId);
  const undoReceivingMutation = useUndoReceivingAction(checkinId);
  const undoReceivingHistoryMutation = useUndoReceivingHistory(checkinId);
  const undoReturnHistoryMutation = useUndoReturnHistory(checkinId);
  const undoCheckHistoryMutation = useUndoCheckHistory(checkinId);
  const reopenShortfallMutation = useReopenShortfallItem(checkinId);
  const undoReturnMutation = useUndoReturnAction(checkinId);
  const requestReturnMutation = useRequestReturn(checkinId);
  const decideReturnMutation = useDecideReturn(checkinId);
  const reopenReturnDecisionMutation = useReopenReturnDecision(checkinId);

  const {
    statusLabel,
    statusColor,
    isUnavailable,
    isReturned,
    canAct,
    canResolveReturn,
    canReplaceReturn,
    canCancelReturn,
    canCorrectReturnDecision,
    canReceive,
    canCloseShortfall,
    receivedTotal,
    pendingQuantity,
    surplusQuantity,
    progressPercent,
    isShortfallClosed,
    expectedQuantity,
    originalExpectedQuantity,
    returnedQuantity,
    returnSummary,
  } = buildCheckinItemDisplay(item, {
    currentUserRole,
    canManageCheckins,
    isClientView,
    readOnly,
  });
  const isPending =
    localActionPending ||
    addReceiptMutation.isPending ||
    closeShortfallMutation.isPending ||
    undoReceivingMutation.isPending ||
    undoReceivingHistoryMutation.isPending ||
    undoReturnHistoryMutation.isPending ||
    undoCheckHistoryMutation.isPending ||
    reopenShortfallMutation.isPending ||
    undoReturnMutation.isPending ||
    requestReturnMutation.isPending ||
    decideReturnMutation.isPending ||
    reopenReturnDecisionMutation.isPending;
  const isLegacyDirectKg = isLegacyDirectKgItem(item);
  const persistedReceivingUndo =
    item.last_receipt?.kind === "RECEIPT" ||
    item.last_receipt?.kind === "SHORTFALL_CLOSE";
  const latestAudit = canAudit
    ? historyQuery.data?.items
        .filter((entry) => entry.payload.checkin_item_id === item.id)
        .sort((left, right) => right.id - left.id)[0]
    : undefined;
  const latestOperation = latestAudit
    ? getCheckinAuditOperation(latestAudit)
    : null;
  const previousReturnState = latestAudit?.payload.previous;
  const auditMatchesReturnLot =
    Boolean(returnSummary?.id) &&
    latestAudit?.payload.return_lot_id === returnSummary?.id;
  // ponytail: admin_helper lacks audit.view; persisted return undo needs a backend eligibility flag.
  const persistedReturnUndo =
    Boolean(
      item.return_lots?.length &&
      auditMatchesReturnLot &&
      previousReturnState &&
      typeof previousReturnState === "object" &&
      "status" in previousReturnState &&
      typeof previousReturnState.status === "string" &&
      previousReturnState.status,
    ) &&
    ((latestOperation === "REQUEST_RETURN" &&
      item.return_status === "DECISION") ||
      (latestOperation === "DECIDE_RETURN" &&
        (item.return_status === "SUBSTITUIDO" ||
          item.return_status === "DEVOLVIDO")));
  const receiptTime = item.last_receipt?.created_at;
  const auditTime = latestAudit?.changed_at;
  const returnTime =
    (returnSummary?.status === "DECISION"
      ? null
      : (item.return_decided_at ??
        item.return_lots?.find((lot) => lot.id === returnSummary?.id)
          ?.decided_at)) ??
    item.return_requested_at ??
    returnSummary?.requestedAt;
  const receiptAuditOrder = compareMovementTime(receiptTime, auditTime);
  const returnAuditOrder = compareMovementTime(returnTime, auditTime);
  const returnReceiptOrder = compareMovementTime(returnTime, receiptTime);
  const auditReceiptKindMatches =
    (latestOperation === "ADD_RECEIPT" &&
      item.last_receipt?.kind === "RECEIPT") ||
    (latestOperation === "CLOSE_SHORTFALL" &&
      item.last_receipt?.kind === "SHORTFALL_CLOSE");
  const auditedReceiptUndo =
    auditReceiptKindMatches &&
    Boolean(item.last_receipt?.id) &&
    latestAudit?.payload.receipt_id === item.last_receipt?.id;
  const receiptIsNewer = receiptAuditOrder === 1;
  const auditIsNewer = receiptAuditOrder === -1;
  const returnAfterReceipt = returnReceiptOrder === 1;
  const auditBehindReturn =
    Boolean(latestAudit && item.return_lots?.length) &&
    (returnAuditOrder === 1 ||
      (returnAuditOrder === 0 && !auditMatchesReturnLot));
  useEffect(() => {
    if (!canAudit || !auditBehindReturn || !returnSummary?.id) return;
    const movementKey = `${returnSummary.id}:${returnTime}`;
    if (lastAuditRefreshMovementKey.current === movementKey) return;
    lastAuditRefreshMovementKey.current = movementKey;
    void historyQuery.refetch();
  }, [
    auditBehindReturn,
    canAudit,
    historyQuery.refetch,
    returnSummary?.id,
    returnTime,
  ]);
  const persistedUndoKind: LastActionKind | null = auditBehindReturn
    ? null // Polling saw a newer return; cached audit cannot establish its undo yet.
    : latestAudit
      ? receiptIsNewer
        ? persistedReceivingUndo
          ? "receiving"
          : null
        : auditIsNewer || !item.last_receipt?.kind
          ? persistedReturnUndo
            ? "return"
            : auditedReceiptUndo
              ? "receiving"
              : null
          : auditedReceiptUndo
            ? "receiving"
            : null
      : item.return_lots?.length &&
          (returnAfterReceipt || returnReceiptOrder === 0)
        ? null // A later or tied return needs audit to establish eligibility.
        : persistedReceivingUndo
          ? "receiving"
          : null;
  const localKind =
    lastActionLabel && lastUndoableItemId === item.id ? lastActionKind : null;
  const localReturnIsNewer =
    localKind === "return" &&
    item.return_status === "DECISION" &&
    returnSummary?.status === "DECISION" &&
    Boolean(localReturnLotId && localReturnLotId === returnSummary?.id) &&
    (!persistedReceivingUndo || returnAfterReceipt) &&
    (!latestAudit || returnAuditOrder === 1);
  const localReceivingFallback =
    localKind === "receiving" &&
    !persistedUndoKind &&
    !latestAudit &&
    !item.last_receipt?.kind &&
    (!item.return_lots?.length || returnReceiptOrder === -1);
  const undoKind = localReturnIsNewer
    ? "return"
    : (persistedUndoKind ?? (localReceivingFallback ? "receiving" : null));
  const actionLabel =
    localReturnIsNewer || localReceivingFallback ? lastActionLabel : null;
  const canUndoRecent =
    canManageCheckins && !readOnly && undoKind !== null && !isPending;
  const lastReceiptLabel = item.last_receipt
    ? timeAgo(item.last_receipt.created_at)
    : item.checked_at
      ? timeAgo(item.checked_at)
      : null;
  const unitLabel = getQuantityUnitLabel(item.unit_type);
  const selectedReceiptSource = selectedHistoryReceipt
    ? receivingHistorySource(selectedHistoryReceipt, item)
    : null;
  const selectedQuantity =
    selectedReceiptSource?.quantity ??
    (typeof selectedHistoryReceipt?.payload.quantity === "number"
      ? selectedHistoryReceipt.payload.quantity
      : null);
  const selectedEventDescription = selectedHistoryReceipt
    ? `${getCheckinAuditOperation(selectedHistoryReceipt) === "CLOSE_SHORTFALL" ? "Encerramento do saldo pendente" : "Recebimento"} de ${selectedQuantity == null ? "quantidade não informada" : `${formatQuantityDisplay(selectedQuantity, item.unit_type)} ${unitLabel}`} registrado em ${new Intl.DateTimeFormat("pt-BR", { dateStyle: "short", timeStyle: "short", timeZone: "America/Sao_Paulo" }).format(new Date(selectedHistoryReceipt.changed_at))}. Desfazer esta ação?`
    : "";
  const selectedReceiptReason = selectedHistoryReceipt
    ? receivingHistoryUndoReason(
        selectedHistoryReceipt,
        item,
        currentUserRole === "super" || currentUserRole === "checker",
        closed || readOnly,
        historyQuery.data?.items ?? [],
      )
    : null;
  const selectedCheckReason = selectedHistoryCheck
    ? checkHistoryUndoReason(
        selectedHistoryCheck,
        historyQuery.data?.items ?? [],
        item,
        currentUserRole === "super" || currentUserRole === "checker",
        closed || readOnly,
      )
    : null;
  const selectedReturnLot = item.return_lots?.find(
    (lot) => lot.id === selectedHistoryReturn?.payload.return_lot_id,
  );
  const selectedReturnDecision =
    selectedHistoryReturn &&
    getCheckinAuditOperation(selectedHistoryReturn) === "DECIDE_RETURN";
  const selectedReturnDescription = selectedHistoryReturn
    ? `${selectedReturnDecision ? "Decisão" : "Solicitação de devolução"} de ${selectedReturnLot ? `${formatQuantityDisplay(selectedReturnLot.quantity, item.unit_type)} ${unitLabel}` : "quantidade não informada"} registrada em ${new Intl.DateTimeFormat("pt-BR", { dateStyle: "short", timeStyle: "short", timeZone: "America/Sao_Paulo" }).format(new Date(selectedHistoryReturn.changed_at))}. A correção restaura o pedido, o alerta e a substituição associados, quando aplicável. Confirmar?`
    : "";
  const factUnit = unitLabel === "cx" ? "caixas" : unitLabel;
  const factQuantity = (quantity: number) =>
    `${formatQuantityDisplay(quantity, item.unit_type)}${factUnit ? ` ${factUnit}` : ""}`;
  const lastReceiptAction =
    item.last_receipt?.kind === "REVERSAL"
      ? "Recebimento desfeito"
      : item.last_receipt?.kind === "SHORTFALL_CLOSE"
        ? "Saldo pendente encerrado"
        : item.last_receipt?.kind === "SHORTFALL_REOPEN"
          ? "Saldo pendente reaberto"
          : item.last_receipt?.kind === "RECEIPT" &&
              item.last_receipt.quantity != null
            ? `${factQuantity(item.last_receipt.quantity)} recebido`
            : "Movimentação registrada";
  const summaryKind =
    undoKind ??
    (returnSummary
      ? !item.last_receipt || returnReceiptOrder === 1
        ? "return"
        : returnReceiptOrder === -1
          ? "receiving"
          : null
      : lastReceiptLabel
        ? "receiving"
        : null);
  const hasPersistedMovement = Boolean(
    (item.last_receipt?.id &&
      receiptTime &&
      Number.isFinite(Date.parse(receiptTime))) ||
    (returnSummary?.id &&
      returnTime &&
      Number.isFinite(Date.parse(returnTime))),
  );
  const hasItemHistory = Boolean(
    historyOpen ||
    hasPersistedMovement ||
    item.receipt_count ||
    item.checked_at ||
    item.return_lots?.length,
  );
  const showLastAction = Boolean(
    actionLabel ||
    (summaryKind && (lastReceiptLabel || returnSummary)) ||
    hasPersistedMovement,
  );
  const completedItem =
    !returnSummary &&
    !isReturned &&
    !isUnavailable &&
    expectedQuantity > 0 &&
    receivedTotal >= expectedQuantity;

  function showActionError(err: unknown) {
    const message = getCheckinActionError(err);
    onFeedback({ title: message, tone: "error" });
  }

  async function receive(quantity: number, label: string) {
    if (!canAct || actionLock.current) return;
    actionLock.current = true;
    setLocalActionPending(true);
    try {
      await addReceiptMutation.mutateAsync({ itemId: item.id, quantity });
      setLastUndoableItemId(item.id);
      setLastActionLabel(label);
      setLastActionKind("receiving");
      setReceivePanelOpen(false);
    } catch (err) {
      showActionError(err);
    } finally {
      actionLock.current = false;
      setLocalActionPending(false);
    }
  }

  async function handleReceiveRemaining() {
    const quantity = pendingQuantity > 0 ? pendingQuantity : expectedQuantity;
    await receive(
      quantity,
      `+${formatQuantityDisplay(quantity, item.unit_type)} recebido`,
    );
  }

  async function handleCloseShortfall(note: string) {
    if (!canAct || actionLock.current) return;
    actionLock.current = true;
    setLocalActionPending(true);
    try {
      await closeShortfallMutation.mutateAsync({ itemId: item.id, note });
      setLastUndoableItemId(item.id);
      setLastActionLabel("saldo encerrado");
      setLastActionKind("receiving");
      setShortfallModalOpen(false);
    } catch (err) {
      showActionError(err);
    } finally {
      actionLock.current = false;
      setLocalActionPending(false);
    }
  }

  async function handleUndo(kind: LastActionKind = lastActionKind) {
    if (!canManageCheckins || readOnly || actionLock.current) return;
    actionLock.current = true;
    setLocalActionPending(true);
    try {
      if (kind === "return") {
        await undoReturnMutation.mutateAsync(item.id);
      } else {
        await undoReceivingMutation.mutateAsync(item.id);
      }
      setLastUndoableItemId((current) =>
        current === item.id ? null : current,
      );
      setLastActionLabel(null);
    } catch (err) {
      showActionError(err);
    } finally {
      actionLock.current = false;
      setLocalActionPending(false);
    }
  }

  async function handleUndoReceivingHistory() {
    const auditId = selectedHistoryReceipt?.id;
    if (
      auditId == null ||
      (currentUserRole !== "super" && currentUserRole !== "checker") ||
      readOnly ||
      closed ||
      actionLock.current
    )
      return;
    actionLock.current = true;
    setLocalActionPending(true);
    try {
      await undoReceivingHistoryMutation.mutateAsync({
        itemId: item.id,
        auditId,
      });
      setLastUndoableItemId(null);
      setLastActionLabel(null);
      setSelectedHistoryReceipt(null);
    } catch (err) {
      showActionError(err);
    } finally {
      actionLock.current = false;
      setLocalActionPending(false);
    }
  }

  async function handleUndoCheckHistory() {
    const event = selectedHistoryCheck;
    if (
      !event ||
      actionLock.current ||
      checkHistoryUndoReason(
        event,
        historyQuery.data?.items ?? [],
        item,
        currentUserRole === "super" || currentUserRole === "checker",
        closed || readOnly,
      )
    )
      return;
    actionLock.current = true;
    setLocalActionPending(true);
    try {
      await undoCheckHistoryMutation.mutateAsync({
        itemId: item.id,
        auditId: event.id,
      });
      setSelectedHistoryCheck(null);
      setLastUndoableItemId(null);
      setLastActionLabel(null);
    } catch (err) {
      showActionError(err);
    } finally {
      actionLock.current = false;
      setLocalActionPending(false);
    }
  }

  async function handleUndoReturnHistory() {
    const event = selectedHistoryReturn;
    if (
      !event ||
      readOnly ||
      actionLock.current ||
      !canUndoReturnHistoryEvent(
        event,
        historyQuery.data?.items ?? [],
        item,
        currentUserRole === "super" || currentUserRole === "checker",
        closed,
      )
    )
      return;
    actionLock.current = true;
    setLocalActionPending(true);
    try {
      await undoReturnHistoryMutation.mutateAsync({
        itemId: item.id,
        auditId: event.id,
      });
      setLastUndoableItemId(null);
      setLastActionLabel(null);
      setSelectedHistoryReturn(null);
    } catch (err) {
      showActionError(err);
    } finally {
      actionLock.current = false;
      setLocalActionPending(false);
    }
  }

  async function handleRequestReturn(quantity: number, note: string) {
    if (!canAct || actionLock.current) return;
    actionLock.current = true;
    setLocalActionPending(true);
    try {
      const updated = await requestReturnMutation.mutateAsync({
        itemId: item.id,
        quantity,
        note,
      });
      setLocalReturnLotId(
        selectLatestActiveReturnLot(
          updated.items?.find((entry) => entry.id === item.id)?.return_lots,
        )?.id ?? null,
      );
      setLastUndoableItemId(item.id);
      setLastActionLabel(
        `${formatQuantityDisplay(quantity, item.unit_type)} devolvidas`,
      );
      setLastActionKind("return");
      setReturnModalOpen(false);
    } catch (err) {
      showActionError(err);
    } finally {
      actionLock.current = false;
      setLocalActionPending(false);
    }
  }

  async function handleReopenShortfall() {
    if (!canAct || actionLock.current) return;
    actionLock.current = true;
    setLocalActionPending(true);
    try {
      await reopenShortfallMutation.mutateAsync(item.id);
      setLastUndoableItemId(null);
      setLastActionLabel(null);
    } catch (err) {
      showActionError(err);
    } finally {
      actionLock.current = false;
      setLocalActionPending(false);
    }
  }

  async function handleReturnResolution(payload: DecideReturnPayload) {
    if (!canResolveReturn || actionLock.current) {
      throw new Error("return decision is no longer available");
    }
    actionLock.current = true;
    try {
      await decideReturnMutation.mutateAsync({
        itemId: item.id,
        ...payload,
      });
    } catch (err) {
      showActionError(err);
      throw err;
    } finally {
      actionLock.current = false;
    }
  }

  async function handleCorrectReturnDecision() {
    if (actionLock.current) return;
    actionLock.current = true;
    setLocalActionPending(true);
    try {
      await reopenReturnDecisionMutation.mutateAsync(item.id);
      setReturnDecisionCorrectionOpen(false);
      setLastUndoableItemId(null);
      setLastActionLabel(null);
    } catch (err) {
      showActionError(err);
    } finally {
      actionLock.current = false;
      setLocalActionPending(false);
    }
  }

  const undoButton =
    canManageCheckins && !readOnly && undoKind ? (
      <button
        type="button"
        className="undo-prominent"
        disabled={!canUndoRecent}
        onClick={() => void handleUndo(undoKind)}
      >
        <RotateCcw size={16} /> Desfazer
      </button>
    ) : null;

  return (
    <article
      className={`check-item ${isReturned || isUnavailable ? "return-item" : returnSummary ? "decision-item" : pendingQuantity > 0 && receivedTotal > 0 ? "warning-item" : completedItem ? "complete-item" : ""}`}
      data-pending={isPending || undefined}
      style={{ "--item-status": statusColor } as React.CSSProperties}
    >
      <div className="item-head">
        <div className="item-identity">
          <div className="item-title-line">
            <h3>{item.product_name}</h3>
            <span className="item-status">
              {isUnavailable
                ? "Indisponível"
                : isReturned
                  ? "Devolvido"
                  : statusLabel}
            </span>
          </div>
          <span className="cell-sub">{item.supplier_name}</span>
          {isLegacyDirectKg ? (
            <p className="item-note">
              Registro histórico — somente visualização.
            </p>
          ) : null}
        </div>
        <div className="item-head-side">
          <div className="received-target">
            <span>Recebido</span>
            <strong>
              {formatQuantityDisplay(receivedTotal, item.unit_type)} /{" "}
              {formatQuantityDisplay(expectedQuantity, item.unit_type)}
              {unitLabel ? ` ${unitLabel}` : ""}
            </strong>
          </div>
        </div>
      </div>
      {originalExpectedQuantity > expectedQuantity ? (
        <p className="item-note">
          Original: {factQuantity(originalExpectedQuantity)}
        </p>
      ) : null}
      <div className="item-progress" aria-hidden="true">
        <i style={{ width: `${progressPercent}%`, background: statusColor }} />
      </div>
      <div className="item-facts">
        <div className="item-fact">
          <small>Esperado</small>
          <strong>{factQuantity(expectedQuantity)}</strong>
        </div>
        <div className="item-fact">
          <small>Recebido</small>
          <strong>{factQuantity(receivedTotal)}</strong>
        </div>
        <div className="item-fact">
          <small>
            {pendingQuantity > 0
              ? "Faltam"
              : surplusQuantity > 0
                ? "Sobra"
                : returnedQuantity > 0
                  ? "Devolvido"
                  : "Última ação"}
          </small>
          <strong>
            {pendingQuantity > 0
              ? factQuantity(pendingQuantity)
              : surplusQuantity > 0
                ? factQuantity(surplusQuantity)
                : returnedQuantity > 0
                  ? factQuantity(returnedQuantity)
                  : (lastReceiptLabel ?? "—")}
          </strong>
        </div>
      </div>
      {returnSummary?.status !== "DECISION" &&
      (item.return_request_note || item.return_decision_note) ? (
        <div className="item-notes">
          {item.return_request_note ? (
            <p>Motivo: {item.return_request_note}</p>
          ) : null}
          {item.return_decision_note ? (
            <p>Decisão: {item.return_decision_note}</p>
          ) : null}
        </div>
      ) : null}

      {((canAudit || currentUserRole === "checker") && hasItemHistory) ||
      (canManageCheckins && !readOnly && undoKind) ||
      hasPersistedMovement ||
      canCorrectReturnDecision ? (
        <div className="history-summary">
          {showLastAction ? (
            <div
              className="last-action-strip"
              data-kind={summaryKind ?? undefined}
            >
              <strong>
                {actionLabel ??
                  (summaryKind === "receiving" && lastReceiptLabel
                    ? `${lastReceiptAction} · ${lastReceiptLabel}`
                    : summaryKind === "return" && returnSummary
                      ? `${factQuantity(returnSummary.quantity)} ${returnSummary.statusLabel.toLowerCase()} · ${timeAgo(returnTime ?? returnSummary.requestedAt)}`
                      : item.last_receipt && returnSummary
                        ? "Movimentações registradas"
                        : lastReceiptLabel
                          ? `${lastReceiptAction} · ${lastReceiptLabel}`
                          : "Movimentações registradas")}
              </strong>
            </div>
          ) : null}
          <div className="history-actions">
            {completedItem ? null : undoButton}
            {(canAudit || currentUserRole === "checker") && hasItemHistory ? (
              <button type="button" onClick={() => setHistoryOpen(true)}>
                <History size={16} /> Ver histórico
                {item.receipt_count ? ` (${item.receipt_count})` : ""}
              </button>
            ) : null}
            {canCorrectReturnDecision ? (
              <button
                className="action-btn checkin-item-action"
                type="button"
                onClick={() => setReturnDecisionCorrectionOpen(true)}
                disabled={isPending}
              >
                <RotateCcw size={16} /> Corrigir decisão
              </button>
            ) : null}
          </div>
        </div>
      ) : null}

      {returnSummary?.status === "DECISION" ? (
        <ReturnHistoryBlock
          item={item}
          summary={returnSummary}
          action={
            canResolveReturn ? (
              <button
                className="action-btn checkin-resolve-return"
                onClick={() => setReturnResolutionOpen(true)}
                disabled={isPending}
              >
                Resolver devolução
              </button>
            ) : null
          }
        />
      ) : null}

      {canAct && !receivePanelOpen ? (
        <div className="item-actions">
          {pendingQuantity > 0 && !isShortfallClosed ? (
            <button
              className="action-btn checkin-item-action checkin-item-action--success"
              onClick={() => void handleReceiveRemaining()}
              disabled={isPending}
            >
              {isPending ? (
                <div className="spinner-small" />
              ) : (
                <Check size={16} />
              )}
              Receber tudo
            </button>
          ) : null}
          {canReceive ? (
            <button
              className="action-btn checkin-item-action checkin-item-action--receive"
              onClick={() => setReceivePanelOpen((value) => !value)}
              disabled={isPending}
            >
              <Plus size={16} />
              Registrar chegada
            </button>
          ) : null}
          {canCloseShortfall ? (
            <button
              className="action-btn checkin-item-action checkin-item-action--danger"
              onClick={() => setShortfallModalOpen(true)}
              disabled={isPending}
            >
              <AlertTriangle size={16} />
              Finalizar com falta
            </button>
          ) : null}
          {isShortfallClosed ? (
            <button
              className="action-btn checkin-item-action checkin-item-action--success"
              onClick={() => void handleReopenShortfall()}
              disabled={isPending}
            >
              <RotateCcw size={16} />
              Reabrir item
            </button>
          ) : (
            <button
              className="action-btn checkin-item-action checkin-item-action--return"
              onClick={() => setReturnModalOpen(true)}
              disabled={isPending}
            >
              <RotateCcw size={16} />
              Registrar devolução
            </button>
          )}
        </div>
      ) : null}
      {completedItem && undoButton ? (
        <div className="item-actions item-actions-secondary">{undoButton}</div>
      ) : null}

      {canReceive ? (
        <ReceivingQuantityPanel
          open={receivePanelOpen}
          item={item}
          pendingQuantity={pendingQuantity}
          initialQuantity={pendingQuantity > 0 ? pendingQuantity : 1}
          pending={isPending}
          onCancel={() => setReceivePanelOpen(false)}
          onValidationError={() => onFeedback(WHOLE_DIRECT_KG_QUANTITY_TOAST)}
          onConfirm={(quantity) =>
            receive(
              quantity,
              `+${formatQuantityDisplay(quantity, item.unit_type)} recebido`,
            )
          }
        />
      ) : null}

      {canAct ? (
        <ReturnNoteModal
          open={shortfallModalOpen}
          title="Encerrar saldo pendente"
          description="Use quando o fornecedor confirmou que o restante não será entregue."
          productName={item.product_name}
          supplierName={item.supplier_name}
          noteLabel="Justificativa"
          notePlaceholder="Ex.: fornecedor confirmou que não enviará o saldo restante."
          confirmLabel="Encerrar saldo"
          confirmColor="#b83232"
          pending={isPending}
          onClose={() => setShortfallModalOpen(false)}
          onConfirm={handleCloseShortfall}
        />
      ) : null}

      <ReturnResolutionModal
        open={returnResolutionOpen}
        item={item}
        allowReplacement={canReplaceReturn}
        allowCancellation={canCancelReturn}
        available={canResolveReturn}
        pending={isPending}
        onClose={() => setReturnResolutionOpen(false)}
        onResolve={handleReturnResolution}
        onSuccess={(result) =>
          onFeedback({
            title: "Decisão registrada.",
            message: result,
            tone: "success",
          })
        }
      />

      <CheckinActionConfirmDialog
        open={returnDecisionCorrectionOpen}
        title="Corrigir decisão de devolução"
        description="A decisão atual será reaberta para que você escolha novamente o destino do item."
        confirmLabel="Reabrir decisão"
        pending={isPending}
        onCancel={() => setReturnDecisionCorrectionOpen(false)}
        onConfirm={() => void handleCorrectReturnDecision()}
      />

      {canAct ? (
        <ReturnQuantityModal
          open={returnModalOpen}
          item={item}
          maxQuantity={Math.max(expectedQuantity, 0)}
          pending={isPending}
          onClose={() => setReturnModalOpen(false)}
          onValidationError={() => onFeedback(WHOLE_DIRECT_KG_QUANTITY_TOAST)}
          onConfirm={handleRequestReturn}
        />
      ) : null}

      <CheckinHistoryDrawer
        open={historyOpen}
        item={item}
        events={historyQuery.data?.items ?? []}
        canUndoReceiving={
          currentUserRole === "super" || currentUserRole === "checker"
        }
        readOnly={readOnly}
        closed={closed}
        isLoading={historyQuery.isLoading}
        error={historyQuery.isError}
        pending={isPending}
        onUndoReceiving={
          canManageCheckins ? setSelectedHistoryReceipt : undefined
        }
        onUndoReturn={canManageCheckins ? setSelectedHistoryReturn : undefined}
        onUndoCheck={
          canAudit || currentUserRole === "checker"
            ? setSelectedHistoryCheck
            : undefined
        }
        onClose={() => setHistoryOpen(false)}
      />
      <CheckinActionConfirmDialog
        open={Boolean(selectedHistoryCheck)}
        title="Desfazer conferência do histórico"
        description={
          selectedHistoryCheck
            ? `Desfazer ${getCheckinAuditOperation(selectedHistoryCheck) === "QUICK_CHECK_ITEM" ? "conferência rápida" : "conferência"} registrada em ${new Intl.DateTimeFormat("pt-BR", { dateStyle: "short", timeStyle: "short", timeZone: "America/Sao_Paulo" }).format(new Date(selectedHistoryCheck.changed_at))}? As quantidades confirmadas deste item voltarão ao estado anterior.`
            : ""
        }
        confirmLabel="Desfazer conferência"
        pending={isPending}
        confirmDisabled={Boolean(
          selectedCheckReason ||
          historyQuery.isError ||
          historyQuery.isFetching,
        )}
        onCancel={() => setSelectedHistoryCheck(null)}
        onConfirm={() => void handleUndoCheckHistory()}
      />
      <CheckinActionConfirmDialog
        open={Boolean(selectedHistoryReceipt)}
        title="Desfazer ação do histórico"
        description={selectedReceiptReason ?? selectedEventDescription}
        confirmLabel="Desfazer"
        pending={isPending}
        confirmDisabled={Boolean(
          selectedReceiptReason ||
          historyQuery.isError ||
          historyQuery.isFetching,
        )}
        onCancel={() => setSelectedHistoryReceipt(null)}
        onConfirm={() => void handleUndoReceivingHistory()}
      />
      <CheckinActionConfirmDialog
        open={Boolean(selectedHistoryReturn)}
        title={
          selectedReturnDecision
            ? "Corrigir decisão do histórico"
            : "Desfazer devolução do histórico"
        }
        description={selectedReturnDescription}
        confirmLabel={
          selectedReturnDecision ? "Corrigir decisão" : "Desfazer devolução"
        }
        pending={isPending}
        confirmDisabled={Boolean(
          selectedHistoryReturn &&
          (readOnly ||
            historyQuery.isError ||
            historyQuery.isFetching ||
            !canUndoReturnHistoryEvent(
              selectedHistoryReturn,
              historyQuery.data?.items ?? [],
              item,
              currentUserRole === "super" || currentUserRole === "checker",
              closed,
            )),
        )}
        onCancel={() => setSelectedHistoryReturn(null)}
        onConfirm={() => void handleUndoReturnHistory()}
      />
    </article>
  );
}

function CheckinDetailBreadcrumb({
  onBack,
  label = "Cargas",
  statusLabel = "Conferência",
}: {
  onBack: () => void;
  label?: string;
  statusLabel?: string;
}) {
  return (
    <nav className="breadcrumb" aria-label="Localização">
      <button type="button" onClick={onBack}>
        <ArrowLeft size={16} /> {label}
      </button>
      <span aria-current="page">/ {statusLabel}</span>
    </nav>
  );
}

function CheckinDetailQueryState({
  state,
  onRetry,
  onBack,
}: {
  state: "loading" | "error";
  onRetry: () => void;
  onBack: () => void;
}) {
  return (
    <OperationalPageFrame
      size="compact"
      className="checkin-detail-page checkin-detail-page--query-state app-page-frame"
    >
      <CheckinDetailBreadcrumb onBack={onBack} />
      <OperationalPageHeader title="Conferência" />
      {state === "loading" ? (
        <>
          <div
            className="checkin-detail-query-progress"
            role="status"
            aria-label="Carregando conferência"
          >
            <span className="checkin-detail-query-progress__label">
              Carregando conferência…
            </span>
            <span
              className="checkin-detail-query-progress__track"
              aria-hidden="true"
            />
          </div>
          <div className="checkin-detail-query-list" aria-hidden="true">
            {Array.from({ length: 3 }, (_, index) => (
              <div key={index} className="checkin-detail-query-row" />
            ))}
          </div>
        </>
      ) : (
        <>
          <p className="checkin-detail-query-error" role="alert">
            Não foi possível carregar a conferência.
          </p>
          <button
            type="button"
            className="checkin-detail-query-retry"
            onClick={onRetry}
          >
            Tentar novamente
          </button>
        </>
      )}
    </OperationalPageFrame>
  );
}

export function CheckinDetailPage() {
  const { checkinId } = useParams({ strict: false }) as { checkinId: string };
  const navigate = useNavigate();
  const { data: currentUser } = useCurrentUser();
  const {
    data: checkin,
    isLoading,
    isError,
    refetch: refetchCheckin,
  } = useCheckin(checkinId);
  const capabilities = getOperationalCapabilities(currentUser);
  const isClientRole =
    currentUser?.role === "client" || currentUser?.role === "client_store";
  const canViewBoxes = Boolean(
    currentUser &&
    !isClientRole &&
    (capabilities.canManageCheckins ||
      capabilities.canAudit ||
      currentUser.role === "supplier"),
  );
  const [activeTab, setActiveTab] = useState<"items" | "boxes">("items");
  const {
    data: suppliers,
    isError: suppliersError,
    refetch: refetchSuppliers,
  } = useSupplierBoxSummaries(checkinId, canViewBoxes && activeTab === "boxes");
  const toggleStatus = useToggleCheckinStatus(checkinId);
  const undoLifecycle = useUndoLifecycleHistory(checkinId);
  const [lifecycleHistoryOpen, setLifecycleHistoryOpen] = useState(false);
  const [selectedLifecycleEvent, setSelectedLifecycleEvent] =
    useState<CheckinAuditEntry | null>(null);
  const [lifecycleUndoError, setLifecycleUndoError] = useState<string | null>(
    null,
  );
  const lifecycleHistory = useCheckinAudit(checkinId, lifecycleHistoryOpen);
  const undoingClosure = selectedLifecycleEvent
    ? getCheckinAuditOperation(selectedLifecycleEvent) === "TOGGLE_STATUS_DONE"
    : false;

  const [searchTerm, setSearchTerm] = useState("");
  const [lastUndoableItemId, setLastUndoableItemId] = useState<string | null>(
    null,
  );
  const [feedbackToast, setFeedbackToast] =
    useState<CheckinFeedbackToast | null>(null);

  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMsg, setAlertMsg] = useState("");
  const [alertPriority, setAlertPriority] = useState<"high" | "medium" | "low">(
    "medium",
  );
  const [alertToast, setAlertToast] = useState(false);
  const [alertSending, setAlertSending] = useState(false);
  const alertLock = useRef(false);
  const [alertError, setAlertError] = useState<string | null>(null);
  const createAlert = useCreateAlert();
  const [lifecycleAction, setLifecycleAction] = useState<
    "done" | "reopened" | null
  >(null);
  const canCreateManualAlert =
    currentUser?.role === "super" || currentUser?.role === "checker";

  useEffect(() => {
    if (!feedbackToast) return;
    const timeout = window.setTimeout(
      () => {
        if (feedbackToast.state === "exiting") {
          setFeedbackToast(null);
          return;
        }
        setFeedbackToast((current) =>
          current ? { ...current, state: "exiting" } : current,
        );
      },
      feedbackToast.state === "exiting"
        ? FEEDBACK_TOAST_EXIT_MS
        : FEEDBACK_TOAST_VISIBLE_MS,
    );
    return () => window.clearTimeout(timeout);
  }, [feedbackToast]);

  function showFeedbackToast(feedback: CheckinFeedback) {
    setFeedbackToast({ ...feedback, state: "visible" });
  }

  function goToCargas() {
    navigate({ to: "/cargas" });
  }

  async function handleSendAlert() {
    if (
      !canCreateManualAlert ||
      !alertMsg.trim() ||
      !checkin ||
      alertLock.current
    )
      return;
    alertLock.current = true;
    setAlertSending(true);
    setAlertError(null);
    try {
      await createAlert.mutateAsync({
        priority: alertPriority,
        message: alertMsg.trim(),
        target_order_id: checkin.order_id,
        checkin_id: checkin.id,
        alert_type: "checker_manual",
      });
      setAlertOpen(false);
      setAlertMsg("");
      setAlertPriority("medium");
      setAlertToast(true);
      setTimeout(() => setAlertToast(false), 3000);
    } catch (err) {
      setAlertError(
        getUserFacingErrorMessage(
          err,
          "Não foi possível salvar a notificação. Tente novamente.",
        ),
      );
    } finally {
      alertLock.current = false;
      setAlertSending(false);
    }
  }

  const filteredItems = useMemo(() => {
    if (!checkin?.items) return [];
    return filterCheckinItems(checkin.items, searchTerm);
  }, [checkin?.items, searchTerm]);

  const filteredSuppliers = useMemo(() => {
    if (!suppliers) return [];
    return filterSupplierBoxSummaries(suppliers, searchTerm);
  }, [suppliers, searchTerm]);

  if (!currentUser) return null;
  if (isLoading && !checkin) {
    return (
      <CheckinDetailQueryState
        state="loading"
        onRetry={refetchCheckin}
        onBack={goToCargas}
      />
    );
  }
  const hasInitialCheckinFailure = isError && !checkin;
  if (hasInitialCheckinFailure || !checkin) {
    return (
      <CheckinDetailQueryState
        state="error"
        onRetry={refetchCheckin}
        onBack={goToCargas}
      />
    );
  }

  const { canAudit, canManageCheckins } = capabilities;
  const isClientView =
    currentUser.role === "client" || currentUser.role === "client_store";
  const isSupplierRole = currentUser.role === "supplier";

  const {
    statusLabel,
    statusColor,
    isSupplierView,
    isReadOnly,
    canManageLifecycle,
    canManageItems,
    canManageBoxes,
    canViewBoxes: displayCanViewBoxes,
    canCreateAlert,
    progressPct,
  } = buildCheckinPageDisplay(checkin, {
    isClientView,
    isSupplierView: isSupplierRole,
    canManageCheckins,
    canAudit,
    role: currentUser.role,
  });

  async function confirmLifecycleAction() {
    if (!lifecycleAction) return;
    try {
      await toggleStatus.mutateAsync(lifecycleAction);
      setLifecycleAction(null);
    } catch (error) {
      showFeedbackToast({
        title: getCheckinActionError(error),
        tone: "error",
      });
    }
  }

  async function confirmLifecycleUndo() {
    if (
      !selectedLifecycleEvent ||
      !checkin ||
      undoLifecycle.isPending ||
      lifecycleHistoryUndoReason(
        selectedLifecycleEvent,
        lifecycleHistory.data?.items ?? [],
        checkin.status,
        (currentUser.role === "super" || currentUser.role === "checker") &&
          canManageLifecycle,
      )
    )
      return;
    setLifecycleUndoError(null);
    try {
      await undoLifecycle.mutateAsync(selectedLifecycleEvent.id);
      setSelectedLifecycleEvent(null);
    } catch (error) {
      setLifecycleUndoError(
        getUserFacingErrorMessage(
          error,
          "Não foi possível desfazer esta alteração. Atualize o histórico e tente novamente.",
        ),
      );
      void lifecycleHistory.refetch();
    }
  }

  return (
    <OperationalPageFrame
      size="compact"
      className="checkin-detail-page detail-page app-page-frame"
    >
      {feedbackToast ? (
        <FloatingToast
          title={feedbackToast.title}
          message={feedbackToast.message}
          tone={feedbackToast.tone}
          state={feedbackToast.state}
          onDismiss={() =>
            setFeedbackToast((current) =>
              current ? { ...current, state: "exiting" } : current,
            )
          }
        />
      ) : null}
      <CheckinDetailBreadcrumb
        onBack={goToCargas}
        label={isSupplierView ? "Cargas" : "Conferências"}
        statusLabel={statusLabel}
      />
      <header className="detail-heading">
        <div>
          <h1>{checkin.client_name}</h1>
          <p>
            {checkin.store_name} · {isSupplierView ? "Carga" : "Conferência"} #
            {checkin.id.slice(0, 8).toUpperCase()}
          </p>
        </div>
        <div className="detail-actions">
          {canCreateAlert ? (
            <button
              type="button"
              className="alert-button"
              onClick={() => {
                setAlertOpen(true);
                setAlertError(null);
              }}
            >
              <AlertTriangle size={18} /> Criar alerta
            </button>
          ) : null}
        </div>
      </header>
      {!isClientView &&
      (currentUser.role === "super" || currentUser.role === "checker") ? (
        <div className="checkin-lifecycle-history-link">
          <button
            type="button"
            className="checkin-lifecycle-history-trigger"
            onClick={() => setLifecycleHistoryOpen(true)}
          >
            <History size={16} /> Ver histórico da carga
          </button>
        </div>
      ) : null}

      <CheckinAlertDialog
        open={alertOpen}
        message={alertMsg}
        priority={alertPriority}
        pending={alertSending}
        error={alertError}
        onMessageChange={setAlertMsg}
        onPriorityChange={setAlertPriority}
        onClose={() => {
          if (alertSending) return;
          setAlertOpen(false);
          setAlertError(null);
        }}
        onConfirm={() => void handleSendAlert()}
      />

      {alertToast ? (
        <div className="checkin-alert-toast" role="status">
          <Check size={14} strokeWidth={3} /> Alerta enviado!
        </div>
      ) : null}

      {checkin.order_deleted && (
        <div className="checkin-detail-deleted-warning">
          <AlertTriangle size={16} strokeWidth={2.5} />
          <span>
            O pedido correspondente foi excluído. As quantidades esperadas foram
            zeradas.
          </span>
        </div>
      )}

      <section className="overall-progress">
        <div className="progress-copy">
          <span>
            {checkin.checked_items} de {checkin.total_items} itens conferidos
          </span>
          <strong>{Math.round(progressPct)}%</strong>
        </div>
        <div className="progress">
          <i style={{ width: `${progressPct}%` }} />
        </div>
      </section>

      <div className="checkin-detail-list">
        <CheckinPageControls
          activeTab={activeTab}
          canShowBoxesTab={displayCanViewBoxes}
          isSupplierView={isSupplierView}
          searchTerm={searchTerm}
          onTabChange={setActiveTab}
          onSearchChange={setSearchTerm}
        />

        <div
          className={`tab-panel ${activeTab === "boxes" && !isClientView ? "boxes-list" : "items-surface"}`}
        >
          {activeTab === "items" || isClientView ? (
            filteredItems.length > 0 ? (
              filteredItems.map((item) => (
                <CheckinItemRow
                  key={item.id}
                  item={item}
                  checkinId={checkinId}
                  canManageCheckins={canManageItems}
                  canAudit={canAudit}
                  currentUserRole={currentUser.role}
                  isClientView={isClientView}
                  readOnly={isReadOnly}
                  closed={checkin.status === "ENCERRADO"}
                  lastUndoableItemId={lastUndoableItemId}
                  setLastUndoableItemId={setLastUndoableItemId}
                  onFeedback={showFeedbackToast}
                />
              ))
            ) : (
              <div className="detail-empty">Nenhum item encontrado.</div>
            )
          ) : !suppliers ? (
            <div
              role={suppliersError ? "alert" : "status"}
              style={{
                textAlign: "center",
                padding: "2rem",
                color: "var(--login-muted)",
                fontSize: "0.875rem",
              }}
            >
              {suppliersError ? (
                <>
                  <p>Não foi possível carregar caixas.</p>
                  <button
                    type="button"
                    className="checkin-detail-query-retry"
                    onClick={() => void refetchSuppliers()}
                  >
                    Tentar novamente
                  </button>
                </>
              ) : (
                "Carregando caixas..."
              )}
            </div>
          ) : (
            <>
              {suppliersError ? (
                <div role="alert" className="checkin-detail-query-error">
                  Não foi possível atualizar caixas. Saldos anteriores podem
                  estar desatualizados.
                  <button
                    type="button"
                    className="checkin-detail-query-retry"
                    onClick={() => void refetchSuppliers()}
                  >
                    Tentar novamente
                  </button>
                </div>
              ) : null}
              {filteredSuppliers.length > 0 ? (
                filteredSuppliers.map((sup) => (
                  <SupplierBoxCard
                    key={sup.supplier_id}
                    supplier={sup}
                    checkinId={checkinId}
                    readOnly={isReadOnly || !canManageBoxes}
                    closed={checkin.status === "ENCERRADO"}
                    canViewHistory={canManageBoxes}
                    stale={suppliersError}
                    onError={(message) =>
                      showFeedbackToast({ title: message, tone: "error" })
                    }
                  />
                ))
              ) : !suppliersError ? (
                <div className="detail-empty">
                  Nenhum fornecedor encontrado.
                </div>
              ) : null}
            </>
          )}
        </div>
      </div>

      {canManageLifecycle && (
        <div className="checkin-lifecycle-actions">
          {checkin.status === "CARREGAMENTO" && (
            <button
              className="checkin-lifecycle-action close"
              onClick={() => setLifecycleAction("done")}
              disabled={toggleStatus.isPending}
            >
              <CheckCircle size={16} strokeWidth={2.5} /> Fechar
            </button>
          )}
          {checkin.status === "ENCERRADO" && (
            <button
              className="checkin-lifecycle-action reopen"
              onClick={() => setLifecycleAction("reopened")}
              disabled={toggleStatus.isPending}
            >
              <RotateCcw size={16} strokeWidth={2.5} /> Reabrir
            </button>
          )}
        </div>
      )}
      <CheckinLifecycleHistoryDrawer
        open={lifecycleHistoryOpen}
        events={lifecycleHistory.data?.items ?? []}
        status={checkin.status}
        canWrite={
          canManageLifecycle &&
          (currentUser.role === "super" || currentUser.role === "checker")
        }
        loading={lifecycleHistory.isLoading}
        error={lifecycleHistory.isError}
        pending={undoLifecycle.isPending}
        onUndo={(event) => {
          setSelectedLifecycleEvent(event);
          setLifecycleUndoError(null);
        }}
        onClose={() => setLifecycleHistoryOpen(false)}
      />
      <CheckinActionConfirmDialog
        open={Boolean(selectedLifecycleEvent)}
        title={
          undoingClosure
            ? "Desfazer fechamento da carga"
            : "Desfazer reabertura da carga"
        }
        description={`Desfazer este evento pode atualizar o pedido, o alerta e as quantidades confirmadas da carga. Confirme apenas se não houve ações posteriores.`}
        confirmLabel={
          undoingClosure ? "Desfazer fechamento" : "Desfazer reabertura"
        }
        pending={undoLifecycle.isPending}
        error={lifecycleUndoError}
        onCancel={() => {
          setSelectedLifecycleEvent(null);
          setLifecycleUndoError(null);
        }}
        onConfirm={() => void confirmLifecycleUndo()}
      />
      <CheckinActionConfirmDialog
        open={lifecycleAction !== null}
        title={
          lifecycleAction === "done"
            ? "Fechar conferência"
            : "Reabrir conferência"
        }
        description={
          lifecycleAction === "done"
            ? "Confirme que a conferência desta carga foi concluída."
            : "A conferência voltará ao estado de carregamento."
        }
        confirmLabel={lifecycleAction === "done" ? "Fechar" : "Reabrir"}
        pending={toggleStatus.isPending}
        onCancel={() => setLifecycleAction(null)}
        onConfirm={() => void confirmLifecycleAction()}
      />
    </OperationalPageFrame>
  );
}
